/**
 * ============================================================================
 * DATA LAYER: REPORTES Y ESTADÍSTICAS (HU-02, HU-03, HU-04)
 * ============================================================================
 * Encargado de realizar las peticiones HTTP al Backend (.NET API) para el
 * módulo administrativo de Reportes. Maneja construcción de endpoints,
 * parámetros de fecha/hora UTC, zona horaria y descarga de archivos binarios (Excel, ZIP).
 */

const getApiBaseUrl = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:50586/api/';
  return url.endsWith('/') ? url : `${url}/`;
};

const API_BASE = `${getApiBaseUrl()}Admin/Reports`;
const REPORTS_API_BASE = `${getApiBaseUrl()}v1/reports`;

/**
 * Convierte un objeto de filtros a query string limpiando valores vacíos o nulos.
 * @param {Object} filter - Filtros de fecha y zona horaria
 * @param {string} [filter.mode] - 'all' o 'match'
 * @param {string|null} [filter.startUtc] - Fecha inicial en formato ISO UTC
 * @param {string|null} [filter.endUtc] - Fecha final en formato ISO UTC
 * @param {string|null} [filter.timezone] - Nombre de la zona horaria seleccionada
 * @returns {string} Query string precedido por '?' o cadena vacía
 */
const buildQueryParams = (filter = {}) => {
  const params = new URLSearchParams();
  const isAllMode = filter.mode === 'all';

  if (!isAllMode && filter.startUtc) params.append('startUtc', filter.startUtc);
  if (!isAllMode && filter.endUtc) params.append('endUtc', filter.endUtc);
  if (filter.timezone) params.append('timezone', filter.timezone);

  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Extrae el nombre de archivo del header HTTP Content-Disposition si está disponible.
 * Soporta formato estándar (filename="...") y codificado RFC 5987 (filename*=UTF-8''...).
 * @param {string|null} contentDisposition - Valor del encabezado Content-Disposition
 * @returns {string|null} Nombre extraído o null
 */
const extractFilenameFromHeader = (contentDisposition) => {
  if (!contentDisposition || typeof contentDisposition !== 'string') return null;

  // 1. Caso RFC 5987 / RFC 6266: filename*=UTF-8''...
  const filenameStarRegex = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i;
  const starMatch = contentDisposition.match(filenameStarRegex);
  if (starMatch && starMatch[1]) {
    try {
      return decodeURIComponent(starMatch[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      return starMatch[1].trim().replace(/^["']|["']$/g, '');
    }
  }

  // 2. Caso estándar: filename="foo.xlsx" o filename=foo.xlsx
  const filenameRegex = /filename="?([^";]+)"?/i;
  const match = contentDisposition.match(filenameRegex);
  if (match && match[1]) {
    return match[1].trim().replace(/^["']|["']$/g, '');
  }

  return null;
};

/**
 * Obtiene el archivo binario Excel (.xlsx) de Mensajes de Apoyo como Blob (HU-03).
 * Realiza fetch a v1/reports/messages/excel enviando startUtc, endUtc y timezone.
 *
 * @param {Object} filter - Filtros temporales
 * @param {string} [filter.mode] - 'all' o 'match'
 * @param {string|null} [filter.startUtc] - Fecha inicial en formato ISO UTC
 * @param {string|null} [filter.endUtc] - Fecha final en formato ISO UTC
 * @param {string|null} [filter.timezone] - Nombre IANA de la zona horaria
 * @returns {Promise<{ success: boolean, blob?: Blob, fileName?: string|null, message?: string }>}
 */
const GetMessagesExcelBlob = async (filter = {}) => {
  try {
    const qs = buildQueryParams(filter);
    const endpoint = `${getApiBaseUrl()}v1/reports/messages/excel${qs}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream',
      },
    });

    if (!response.ok) {
      let errorMessage = 'No fue posible descargar el reporte de mensajes en Excel.';
      try {
        const errorData = await response.json();
        if (errorData && (errorData.message || errorData.title)) {
          errorMessage = errorData.message || errorData.title;
        }
      } catch {
        // En caso de respuesta de error no JSON
      }

      return {
        success: false,
        message: errorMessage,
      };
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    const fileName = extractFilenameFromHeader(contentDisposition);

    return {
      success: true,
      blob,
      fileName,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Error de conexión al descargar el reporte de mensajes en Excel.',
    };
  }
};

/**
 * Obtiene el archivo empaquetado ZIP con las fotografías de Fan Cam como Blob (HU-04).
 * Realiza fetch a ${REPORTS_API_BASE}/fancam/zip enviando startUtc, endUtc y timezone.
 *
 * @param {Object} filter - Filtros temporales
 * @param {string|null} [filter.startUtc] - Fecha inicial en formato ISO UTC
 * @param {string|null} [filter.endUtc] - Fecha final en formato ISO UTC
 * @param {string|null} [filter.timezone] - Nombre IANA de la zona horaria
 * @returns {Promise<{ success: boolean, blob?: Blob, fileName?: string|null, error?: string, message?: string }>}
 */
const GetFanCamZipBlob = async (filter = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filter?.startUtc) queryParams.append('startUtc', filter.startUtc);
    if (filter?.endUtc) queryParams.append('endUtc', filter.endUtc);
    if (filter?.timezone) queryParams.append('timezone', filter.timezone);

    const queryString = queryParams.toString();
    const endpoint = `${REPORTS_API_BASE}/fancam/zip?${queryString}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        Accept: 'application/zip',
      },
    });

    if (!response.ok) {
      let errorMessage = 'No fue posible descargar el archivo ZIP de Fan Cam.';
      try {
        const errorData = await response.json();
        if (errorData && (errorData.message || errorData.title)) {
          errorMessage = errorData.message || errorData.title;
        }
      } catch {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch {}
      }

      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
      };
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    const fileName = extractFilenameFromHeader(contentDisposition);

    return {
      success: true,
      blob,
      fileName,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error de conexión al descargar el archivo ZIP de Fan Cam.',
      message: error.message || 'Error de conexión al descargar el archivo ZIP de Fan Cam.',
    };
  }
};


/**
 * Obtiene el resumen consolidado de métricas (total selfies, mensajes, etc.)
 * según el rango UTC y zona horaria especificados.
 * @param {Object} filter - Filtros temporales
 * @returns {Promise<{ success: boolean, data?: any, message?: string }>}
 */
const GetReportsSummary = async (filter = {}) => {
  try {
    const qs = buildQueryParams(filter);
    const endpoint = `${getApiBaseUrl()}v1/reports/summary${qs}`;
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || 'No fue posible obtener el resumen de reportes.',
      };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: 'Error de conexión al obtener el resumen de reportes.',
    };
  }
};

/**
 * Solicita la generación o consulta del reporte detallado de Fan Cam.
 * @param {Object} filter - Filtros temporales
 * @returns {Promise<{ success: boolean, data?: any, message?: string }>}
 */
const GenerateFanCamReport = async (filter = {}) => {
  try {
    const isAllMode = filter.mode === 'all';
    const response = await fetch(`${API_BASE}/fancam`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        startUtc: isAllMode ? null : filter.startUtc || null,
        endUtc: isAllMode ? null : filter.endUtc || null,
        timezone: filter.timezone || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || 'Error al generar reporte de Fan Cam.',
      };
    }

    const data = await response.json().catch(() => ({}));
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: 'Error de conexión al solicitar el reporte de Fan Cam.',
    };
  }
};

/**
 * Solicita la generación o consulta del reporte de Mensajes de Apoyo.
 * @param {Object} filter - Filtros temporales
 * @returns {Promise<{ success: boolean, data?: any, message?: string }>}
 */
const GenerateMessagesReport = async (filter = {}) => {
  try {
    const isAllMode = filter.mode === 'all';
    const response = await fetch(`${API_BASE}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        startUtc: isAllMode ? null : filter.startUtc || null,
        endUtc: isAllMode ? null : filter.endUtc || null,
        timezone: filter.timezone || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || 'Error al generar reporte de mensajes.',
      };
    }

    const data = await response.json().catch(() => ({}));
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: 'Error de conexión al solicitar el reporte de mensajes.',
    };
  }
};

/**
 * Solicita la exportación del reporte global consolidado.
 * @param {Object} filter - Filtros temporales
 * @returns {Promise<{ success: boolean, data?: any, message?: string }>}
 */
const GenerateGlobalReport = async (filter = {}) => {
  try {
    const isAllMode = filter.mode === 'all';
    const response = await fetch(`${API_BASE}/global`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        startUtc: isAllMode ? null : filter.startUtc || null,
        endUtc: isAllMode ? null : filter.endUtc || null,
        timezone: filter.timezone || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || 'Error al exportar reporte global.',
      };
    }

    const data = await response.json().catch(() => ({}));
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: 'Error de conexión al exportar reporte global.',
    };
  }
};

/**
 * Obtiene el estado o URL final de descarga de un archivo generado.
 * @param {string} reportId - ID del reporte
 * @returns {Promise<{ success: boolean, data?: any, message?: string }>}
 */
const GetReportStatus = async (reportId) => {
  try {
    const response = await fetch(`${API_BASE}/status/${encodeURIComponent(reportId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || 'Error al consultar estado del reporte.',
      };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: 'Error de conexión al consultar el estado del reporte.',
    };
  }
};

/**
 * Dispara la generación en segundo plano del Respaldo Maestro ZIP (HU-05).
 * Realiza petición POST a ${REPORTS_API_BASE}/master-zip con startUtc, endUtc y timezone.
 *
 * @param {Object} filter - Filtros temporales
 * @param {string|null} [filter.startUtc] - Fecha inicial en ISO UTC
 * @param {string|null} [filter.endUtc] - Fecha final en ISO UTC
 * @param {string|null} [filter.timezone] - Zona horaria
 * @returns {Promise<{ success: boolean, token?: string, fileName?: string, message?: string, isConflict?: boolean }>}
 */
const TriggerMasterZipGeneration = async (filter = {}) => {
  try {
    const response = await fetch(`${REPORTS_API_BASE}/master-zip`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        startUtc: filter?.startUtc || null,
        endUtc: filter?.endUtc || null,
        timezone: filter?.timezone || null,
      }),
    });

    if (!response.ok) {
      let errorMessage = 'No fue posible iniciar la generación del Respaldo Maestro.';
      try {
        const errorData = await response.json();
        if (errorData && (errorData.message || errorData.title)) {
          errorMessage = errorData.message || errorData.title;
        }
      } catch {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch {}
      }

      return {
        success: false,
        message: errorMessage,
        isConflict: response.status === 409,
      };
    }

    const data = await response.json();
    return {
      success: true,
      token: data.token,
      fileName: data.fileName,
      message: data.message,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Error de conexión al solicitar el Respaldo Maestro.',
      isConflict: false,
    };
  }
};

/**
 * Consulta el estado o progreso de la generación del Respaldo Maestro ZIP (HU-05).
 * Realiza petición GET a ${REPORTS_API_BASE}/status/${token}.
 *
 * @param {string} token - Identificador del trabajo de generación
 * @returns {Promise<Object>} Objeto de progreso devuelto por la API
 */
const GetMasterZipStatus = async (token) => {
  try {
    const response = await fetch(`${REPORTS_API_BASE}/status/${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let errorMessage = 'Error al consultar el estado del reporte maestro.';
      try {
        const errorData = await response.json();
        if (errorData && (errorData.message || errorData.title)) {
          errorMessage = errorData.message || errorData.title;
        }
      } catch {}

      return {
        success: false,
        message: errorMessage,
      };
    }

    const data = await response.json();
    return {
      success: true,
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Error de conexión al consultar el estado del reporte maestro.',
    };
  }
};

/**
 * Descarga el archivo binario generado y listo como Blob (HU-05).
 * Realiza fetch con método GET a ${getApiBaseUrl()}${downloadUrl.replace(/^\/?api\/?/, '').replace(/^\//, '')}.
 *
 * @param {string} downloadUrl - URL relativa devuelta por el backend
 * @returns {Promise<{ success: boolean, blob?: Blob, fileName?: string|null, message?: string }>}
 */
const DownloadReadyReportBlob = async (downloadUrl) => {
  try {
    const cleanPath = (downloadUrl || '').replace(/^\/?api\/?/, '').replace(/^\//, '');
    const endpoint = `${getApiBaseUrl()}${cleanPath}`;

    const response = await fetch(endpoint, {
      method: 'GET',
    });

    if (!response.ok) {
      let errorMessage = 'No fue posible descargar el archivo listo.';
      try {
        const errorData = await response.json();
        if (errorData && (errorData.message || errorData.title)) {
          errorMessage = errorData.message || errorData.title;
        }
      } catch {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch {}
      }

      return {
        success: false,
        message: errorMessage,
      };
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    const fileName = extractFilenameFromHeader(contentDisposition);

    return {
      success: true,
      blob,
      fileName,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Error de conexión al descargar el archivo de reporte.',
    };
  }
};

export {
  GetFanCamZipBlob,
  GetMessagesExcelBlob,
  GetReportsSummary,
  GenerateFanCamReport,
  GenerateMessagesReport,
  GenerateGlobalReport,
  GetReportStatus,
  TriggerMasterZipGeneration,
  GetMasterZipStatus,
  DownloadReadyReportBlob,
};
